/**
 * Shared hook returning the global viewport shading settings used by every
 * on-screen renderer (primitives, entities, mannequins).
 *
 * Before this hook existed, each renderer called `useStore((s) => s.field)`
 * ~10 times to read the same global flags (viewMode, matcapPreset, shadows,
 * etc.).  With N objects in the scene, that meant N × 10 subscribers to the
 * store, each re-running its own selector on every mutation.  Consolidating
 * into a single `useShallow` selector collapses those into one subscriber
 * per renderer, and gives stable referential identity: the returned object
 * is only replaced when one of the underlying fields actually changes, so
 * downstream `useMemo` / `useEffect` deps can depend on the struct itself
 * (or on its destructured fields) without extra churn.
 */

import { useShallow } from "zustand/react/shallow";
import { selectActiveRenderConfig } from "../app/renderConfig.ts";
import { useStore } from "../app/store.ts";
import type {
  RenderGridOverride,
  ViewMode,
  WorkspaceMode,
} from "../app/storeTypes.ts";
import type { DebugMaterialConfig } from "./debugMaterial.ts";

export interface ViewportShading {
  viewMode: ViewMode;
  matcapPreset: string;
  shadowsEnabled: boolean;
  showWireframe: boolean;
  objectXray: boolean;
  wireframeXray: boolean;
  showTextures: boolean;
  textureMapsEnabled: boolean;
  debugMaterial: DebugMaterialConfig | null;
  workspaceMode: WorkspaceMode;
  renderGridOverride: RenderGridOverride;
}

export function useViewportShading(): ViewportShading {
  return useStore(
    useShallow((s) => {
      const cfg = selectActiveRenderConfig(s);
      return {
        viewMode: cfg.viewMode,
        matcapPreset: cfg.matcapPreset,
        shadowsEnabled: cfg.shadowsEnabled,
        showWireframe: cfg.showWireframe,
        wireframeXray: cfg.wireframeXray,
        objectXray: s.objectXray,
        showTextures: s.showTextures,
        textureMapsEnabled: s.textureMapsEnabled,
        debugMaterial: s.debugMaterial,
        workspaceMode: s.workspaceMode,
        renderGridOverride: s.renderGridOverride,
      };
    }),
  );
}
